import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTenant } from "../../Contexts/TenantContext";
import { useAuth } from "../../Contexts/AuthContext";
import { useCart } from "../../Contexts/CartContext";
import { EditUserDataType } from "../../Enums/EditUserDataType";
import { formatPhoneNumber } from "../../Utils/DataFormatters";
import { FaPhone, FaClock, FaUser, FaShoppingCart, FaSignOutAlt, FaLock } from 'react-icons/fa';
import SearchBar from "../SearchBar/SearchBar";
import CartSidebar from "../CartSidebar/CartSidebar";
import AdminMenu from "../AdminMenu/AdminMenu";
import AdminNotificationBell from "../AdminNotificationBell/AdminNotificationBell";
import OperatorMenu from "../OperatorMenu/OperatorMenu";
import UserMenu from "../UserMenu/UserMenu";
import ProductList from "../ProductList/ProductList";
import EditUserDataFlow from "../EditUserDataFlow/EditUserDataFlow";
import UserAccountMenu from "../UserAccountMenu/UserAccountMenu";
import CartAuthModal from "../CartAuthModal/CartAuthModal";
import MobileSidebar, { SidebarItem } from "../MobileSidebar/MobileSidebar";
import AdminMobileSidebar from "../AdminMobileSidebar/AdminMobileSidebar";
import { TenantPreference } from "../../Models/TenantPreference";

type FeatureKey = "enableOperatorsFeature" | "enableBrandsFeature" | "enableCategoriesFeature" | "enableModelsFeature";
interface AdminNavItem {
  to: string;
  label: string;
  requiresPreference?: FeatureKey;
}

const ADMIN_ITEMS: AdminNavItem[] = [
  { to: "/admin",             label: "Início" },
  { to: "/admin/operadores",  label: "Operadores", requiresPreference: "enableOperatorsFeature" },
  { to: "/admin/clientes",    label: "Clientes" },
  { to: "/admin/marcas",      label: "Marcas",     requiresPreference: "enableBrandsFeature" },
  { to: "/admin/categorias",  label: "Categorias", requiresPreference: "enableCategoriesFeature" },
  { to: "/admin/modelos",     label: "Modelos",    requiresPreference: "enableModelsFeature" },
  { to: "/admin/produtos",    label: "Produtos" },
  { to: "/admin/pedidos",     label: "Pedidos" },
  { to: "/admin/cupons",      label: "Cupons" },
  { to: "/admin/whatsapp",    label: "WhatsApp" },
  { to: "/admin/faturamento", label: "Faturamento" },
  { to: "/admin/balcao",      label: "Balcão" },
];

const OPERATOR_ITEMS: AdminNavItem[] = [
  { to: "/operador",            label: "Início" },
  { to: "/operador/clientes",   label: "Clientes" },
  { to: "/operador/pedidos",    label: "Pedidos" },
  { to: "/operador/whatsapp",   label: "WhatsApp" },
  { to: "/operador/balcao",     label: "Balcão" },
];

const Navbar = () => {
  const location = useLocation();

  const navigate = useNavigate();

  const { clearCart } = useCart();

  const { tenant, tenantContact, categories, tenantPreference, loading: isTenantLoading } = useTenant();
  const { user, isAdmin, isOperator, isGoogleUser, logout } = useAuth();
  const { cartItems } = useCart();

  const [search, setSearch] = useState('');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isNavCartAuthOpen, setIsNavCartAuthOpen] = useState(false);
  const [navCartAuthTab, setNavCartAuthTab] = useState<'login' | 'register'>('login');
  const [isEditUserDataFlowOpen, setIsEditUserDataFlowOpen] = useState(false);
  const [editUserDataMethod, setEditUserDataMethod] = useState<EditUserDataType>(EditUserDataType.Password);
  const userDropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const accountHoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const accountDropdownRef = useRef<HTMLDivElement | null>(null);
  const isCartPage = location.pathname === '/carrinho';

  useEffect(() => {
    window.scrollTo({ 
      top: 0,
      behavior: "smooth"
    });
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;

      if (accountDropdownRef.current && !accountDropdownRef.current.contains(targetNode)) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);

      const userT = userDropdownTimeoutRef.current;
      if (userT) {
        clearTimeout(userT);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
      const accountT = accountHoverTimeoutRef.current;
      if (accountT) {
        clearTimeout(accountT);
      }
    };
  }, []);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleOpenCart = () => {
    setIsCartOpen(true);
  };

  const handleLogout = async () => {
    const onCartPage = location.pathname.includes("/carrinho");
    const cartEmpty = cartItems.length === 0;
    if (!onCartPage) {
      clearCart();
    }
    await logout();
    if (!onCartPage || cartEmpty) {
      navigate("/");
    }
  };

  const filteredCategories = categories.filter(
    (c) => !c.name.toLowerCase().includes("orçamento")
  );

  const userSidebarItems: SidebarItem[] = [
    { kind: "link", path: "/", label: "INÍCIO" },
    { kind: "link", path: "/sobre", label: "A EMPRESA" },
    { kind: "products", label: "PRODUTOS", basePath: "/produtos", allLabel: "Todos os produtos" },
    ...(tenantPreference?.enableTechnicalAssistancePage
      ? [{ kind: "link", path: "/assistencia-tecnica", label: "ASSISTÊNCIA TÉCNICA" } as SidebarItem]
      : []),
    { kind: "link", path: "/contato", label: "CONTATO" },
  ];

  const adminItems = (isAdmin ? ADMIN_ITEMS : OPERATOR_ITEMS).filter((item) =>
    item.requiresPreference
      ? (tenantPreference?.[item.requiresPreference as keyof TenantPreference] as boolean | undefined) !== false
      : true
  );

  const isAdminActive = (to: string) =>
    to === "/admin" || to === "/operador"
      ? location.pathname === to
      : location.pathname === to || location.pathname.startsWith(`${to}/`);

  const handleAdminNav = (to: string) => (event: React.MouseEvent) => {
    if (isAdminActive(to)) {
      event.preventDefault();
      navigate(to, { replace: true, state: { refreshKey: Date.now() } });
    }
  };

  const isReady = !!tenant && !isTenantLoading;

  if (!isReady) {
    return (
      <div className="w-full shadow bg-primary-600 text-white">
        <div className="w-full px-4 sm:px-7 md:px-16 py-5 flex items-center justify-between animate-pulse gap-4">
          <div className="h-10 w-32 bg-white/30 rounded" />
          <div className="flex-1 mx-6 h-4 bg-white/20 rounded" />
          <div className="h-8 w-24 bg-white/30 rounded" />
        </div>
      </div>
    );
  }

  const isStaff = isAdmin || isOperator;
  const staffHome = isAdmin ? "/admin" : "/operador";
  const hasTopContactInfo = !!tenantContact?.whatsAppNumber || !!tenant?.workingHours;

  return (
    <>
      {!isStaff && <div className="hidden lg:block w-full shadow bg-white">
        <div className={`w-full px-4 sm:px-7 md:px-16 py-4 flex flex-col items-center gap-3 sm:flex-row justify-center md:py-10 ${
          hasTopContactInfo ? "lg:justify-between" : ""
        }`}>
          {tenant?.logo && (
            <div className="flex items-center justify-center lg:justify-start">
              <Link to="/" className="flex justify-center">
                <img
                  src={tenant.logo.url}
                  alt="Logo"
                  className="h-12 md:h-16"
                />
              </Link>
            </div>
          )}

          <div className="hidden lg:flex lg:items-center lg:gap-10 text-xs md:text-sm">
            {tenantContact?.whatsAppNumber &&
            (
              <>
                <div className="flex items-center gap-2">
                  <div className="bg-primary-600 p-2 rounded text-white">
                    <FaPhone />
                  </div>
                  <div>
                    <p className="font-bold">WHATSAPP:</p>
                    <p>{formatPhoneNumber(tenantContact?.whatsAppNumber)}</p>
                  </div>
                </div>

                <div className="hidden md:flex md:border-l md:h-12"></div>
              </>
            )}

            {tenant?.workingHours &&
            (
              <div className="flex items-center gap-2">
                <div className="bg-primary-600 p-2 rounded text-white">
                  <FaClock />
                </div>
                <div>
                  <p className="font-bold">HORÁRIOS:</p>
                  <p>{tenant?.workingHours}</p>    
                </div>
              </div>
            )}
          </div>
        </div>
      </div>}

      <nav className="bg-primary-600 text-white shadow relative sm:z-40">
        <div className={`w-full flex items-center px-4 sm:px-7 gap-3 sm:gap-4 ${isStaff ? "flex-nowrap py-3 min-h-16 lg:px-10 lg:py-5" : "flex-wrap lg:flex-nowrap gap-y-5 sm:gap-y-5 lg:gap-x-6 py-4 lg:py-3 min-h-16 lg:min-h-14 md:px-16"}`}>
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => { setIsMobileMenuOpen((prev) => !prev); setSearch(''); }}
              className={`text-white lg:hidden flex items-center justify-center w-8 h-8 transition-opacity hover:opacity-75 ${isStaff ? "" : "-ml-2"}`}
              aria-label="Abrir menu"
            >
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <rect x="3" y="5" width="18" height="2" rx="1" fill="currentColor" />
                <rect x="3" y="11" width="18" height="2" rx="1" fill="currentColor" />
                <rect x="3" y="17" width="18" height="2" rx="1" fill="currentColor" />
              </svg>
            </button>

            {/* The bar is the primary color, which a logo in that color would disappear into */}
            {isStaff && (
              <Link
                to={staffHome}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 lg:static lg:translate-x-0 lg:translate-y-0 shrink-0 flex items-center bg-white rounded px-2 py-1"
              >
                {tenant?.logo ? (
                  <img src={tenant.logo.url} alt="Logo" className="h-6 min-[400px]:h-7 sm:h-8 lg:h-10 w-auto" />
                ) : (
                  <span className="text-sm font-bold text-primary-600">{tenant?.companyName}</span>
                )}
              </Link>
            )}

            {!isStaff && tenant?.logo && (
              <Link
                to="/"
                className="lg:hidden absolute left-1/2 top-8 -translate-x-1/2 -translate-y-1/2 flex items-center bg-white rounded px-2 py-1"
              >
                <img src={tenant.logo.url} alt="Logo" className="h-9 w-auto" />
              </Link>
            )}

            {!isStaff && (
              <div className="hidden lg:flex">
                <UserMenu onNavigate={() => setIsMobileMenuOpen(false)} />
              </div>
            )}
          </div>

          {isStaff && (
            <div className="hidden lg:flex flex-1 min-w-0 justify-center">
              {isAdmin ? <AdminMenu /> : <OperatorMenu />}
            </div>
          )}

          {!isAdmin && !isOperator && (
            <div className={`${isCartPage ? "hidden lg:flex" : "flex"} order-last lg:order-none basis-full lg:basis-auto lg:flex-1 items-center justify-center lg:justify-end`}>
              {!isCartPage && (
                <div className="relative w-full min-w-[200px] lg:max-w-[435px]">
                  <SearchBar onSearch={handleSearch} className="w-full" inputClassName="!py-[9px] !text-sm lg:!py-2" />
                  {search && (
                    <div className="absolute top-full left-0 w-full bg-white shadow z-50">
                      <ProductList search={search} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className={`flex items-center gap-3 sm:gap-5 ml-auto shrink-0 ${isStaff ? "" : "lg:gap-6"}`}>
            {!isAdmin && !isOperator ? (
              <>
                <div
                  className="relative hidden lg:flex flex-col items-center"
                  onMouseEnter={() => {
                    if (userDropdownTimeoutRef.current) {
                      clearTimeout(userDropdownTimeoutRef.current);
                      userDropdownTimeoutRef.current = null;
                    }
                    setIsUserDropdownOpen(true);
                  }}
                  onMouseLeave={() => {
                    if (userDropdownTimeoutRef.current) {
                      clearTimeout(userDropdownTimeoutRef.current);
                    }
                    userDropdownTimeoutRef.current = setTimeout(() => {
                      setIsUserDropdownOpen(false);
                    }, 200);
                  }}
                >
                  <button className="text-white hover:text-gray-300 text-xl">
                    <FaUser />
                  </button>

                  {isUserDropdownOpen && (
                    <div
                      className="absolute top-full right-0 h-10 w-40"
                      onMouseEnter={() => {
                        if (userDropdownTimeoutRef.current) {
                          clearTimeout(userDropdownTimeoutRef.current);
                          userDropdownTimeoutRef.current = null;
                        }
                        setIsUserDropdownOpen(true);
                      }}
                      onMouseLeave={() => {
                        if (userDropdownTimeoutRef.current) {
                          clearTimeout(userDropdownTimeoutRef.current);
                        }
                        userDropdownTimeoutRef.current = setTimeout(() => {
                          setIsUserDropdownOpen(false);
                        }, 200);
                      }}
                    />
                  )}

                  {isUserDropdownOpen && (
                    <div
                      className="absolute -right-6 top-full mt-4 w-36 bg-white shadow-xl rounded-md border z-50 p-1 sm:w-48 sm:p-2"
                      onMouseEnter={() => {
                        if (userDropdownTimeoutRef.current) {
                          clearTimeout(userDropdownTimeoutRef.current);
                          userDropdownTimeoutRef.current = null;
                        }
                        setIsUserDropdownOpen(true);
                      }}
                      onMouseLeave={() => {
                        if (userDropdownTimeoutRef.current) {
                          clearTimeout(userDropdownTimeoutRef.current);
                        }
                        userDropdownTimeoutRef.current = setTimeout(() => {
                          setIsUserDropdownOpen(false);
                        }, 200);
                      }}
                    >
                      <div className="absolute -top-2 right-6 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-200" />
                      <UserAccountMenu
                        onClose={() => setIsUserDropdownOpen(false)}
                        onLogout={handleLogout}
                        onCartAuth={isCartPage ? (tab) => { setNavCartAuthTab(tab); setIsNavCartAuthOpen(true); } : undefined}
                      />
                    </div>
                  )}
                </div>
                
                {!isCartPage && (
                  <button 
                    className="flex gap-2 text-white hover:text-gray-300 text-xl"
                    onClick={handleOpenCart}
                  >
                    <FaShoppingCart />
                    <div className="text-xs border-700 bg-black rounded-full px-2 py-1">
                      {cartItems.length}
                    </div>
                  </button>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2 sm:gap-5 lg:gap-8">
                <AdminNotificationBell className="text-white hover:text-gray-300" />
                <span className="lg:hidden max-[359px]:hidden text-[11px] min-[400px]:text-xs font-bold text-black bg-white px-1.5 min-[400px]:px-2 py-1 rounded-full">
                  {isOperator ? "Operador" : "Admin"}
                </span>
                <div className="hidden lg:flex items-center gap-8">
                  {!isGoogleUser && (
                    <button
                      className="flex items-center justify-center w-5 h-5 text-white hover:text-gray-300"
                      aria-label="Alterar senha"
                      title="Alterar senha"
                      onClick={() => {
                        setEditUserDataMethod(EditUserDataType.Password);
                        setIsEditUserDataFlowOpen(true);
                      }}
                    >
                      <FaLock size={18} />
                    </button>
                  )}
                  <button
                    className="flex items-center justify-center w-5 h-5 text-white hover:text-gray-300"
                    aria-label="Sair"
                    title="Sair"
                    onClick={handleLogout}
                  >
                    <FaSignOutAlt size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      </nav>

      {isAdmin || isOperator ? (
        <AdminMobileSidebar
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          items={adminItems}
          isActive={isAdminActive}
          handleNav={handleAdminNav}
          handleLogout={handleLogout}
          isGoogleUser={isGoogleUser}
          onChangePassword={() => {
            setEditUserDataMethod(EditUserDataType.Password);
            setIsEditUserDataFlowOpen(true);
          }}
          userName={user?.firstName}
        />
      ) : (
        <MobileSidebar
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          navLinks={[]}
          filteredCategories={filteredCategories}
          handleLogout={handleLogout}
          items={userSidebarItems}
          breakpoint="lg"
        />
      )}

      <CartSidebar
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
      />
      <EditUserDataFlow
        isOpen={isEditUserDataFlowOpen}
        onClose={() => setIsEditUserDataFlowOpen(false)}
        method={editUserDataMethod}
      />
      <CartAuthModal
        isOpen={isNavCartAuthOpen}
        onClose={() => setIsNavCartAuthOpen(false)}
        onAuthSuccess={() => setIsNavCartAuthOpen(false)}
        initialTab={navCartAuthTab}
      />
    </>
  );
};

export default Navbar;
